// enci.pageLoader.js

import { debugLog } from './enci.log.js';
import { waitForElement } from './enci.utils.js';

/**
 * Initialize the page by dynamically loading the corresponding module based on the page name.
 * @param {string} pageName - The name of the page to initialize.
 * @returns {Promise<void>}F
 */
export async function initializePage(pageName) {
    debugLog(`initializePage >> Attempting to load page: ${pageName}`);

    try {
        const module = await importModuleForPage(pageName);
        await executeModuleLoad(module, 'loadPage', pageName);
    } catch (error) {
        console.error(`initializePage >> Error loading module for ${pageName}: `, error);
    }
}

/**
 * Import the module for a given page.
 * @param {string} pageName - The name of the page to import the module for.
 * @returns {Promise<Object>} The imported module.
 * @throws {Error} If the page is not found.
 */
async function importModuleForPage(pageName) {
    switch (pageName) {
        case 'ContactRequestAdmin':
            return import('./Admin/enci.portal.contactrequests.js');
        case 'DevexAdmin':
            return import('./enci.devexdemos.js');
        case 'EmailAdmin':
            return import('./enci.htmleditor.js');
        case 'FileManagerAdmin':
            return import('./enci.filemanagerremote.js');
        case 'WikiPageShell':
        case 'WikiSearchIntro':
            return import('./enci.wiki.js');
        default:
            throw new Error('Page not found');
    }
}

/**
 * Initialize a component by dynamically loading the corresponding module based on the component name.
 * @param {string} componentName - The name of the component to initialize.
 * @param {boolean} isAdmin - Whether the component is being loaded in an admin context.
 * @param {string|null} [cssSelector=null] - The CSS selector to be applied.
 * @returns {Promise<void>}
 */
export async function initializeComponent(componentName, isAdmin, cssSelector = null, config = {}) {
    debugLog(`initializeComponent >> Attempting to load component: ${componentName}`);

    try {
        const module = await import(
      /* webpackChunkName: "component-[request]" */ `./Section/${componentName}.js`
        );

        document.dispatchEvent(new CustomEvent(`${componentName}Ready`));

        if (cssSelector) {
            await waitForElement(cssSelector);
        }

        await executeModuleLoad(module, 'loadComponent', componentName, isAdmin, cssSelector, config);
    } catch (error) {
        console.error(`initializeComponent >> Error loading module for ${componentName}: `, error);

        // Attempt recovery
        setTimeout(async () => {
            try {
                debugLog(`initializeComponent >> Attempting recovery for ${componentName}`);
                await initializeComponent(componentName, isAdmin, cssSelector, config);
            } catch (recoveryError) {
                console.error(
                    `initializeComponent >> Recovery failed for ${componentName}: `,
                    recoveryError
                );
            }
        }, 1000);
    }
}

/**
 * Initialize an admin component by dynamically loading the corresponding module based on the component name.
 * @param {string} componentName - The name of the admin component to initialize.
 * @param {object} dotNetHelper - The .NET helper object.
 * @returns {Promise<void>}
 */
export async function initializeAdminComponent(componentName, dotNetHelper) {
    debugLog(`initializeAdminComponent >> Attempting to load component: ${componentName}`);

    try {
        const module = await importModuleForAdminComponent(componentName);
        await executeModuleLoad(module, 'loadComponent', componentName, dotNetHelper);
    } catch (error) {
        console.error(`initializeAdminComponent >> Failed to load component ${componentName}: `, error);
    }
}

/**
 * Import the module for a given admin component.
 * @param {string} componentName - The name of the admin component to import the module for.
 * @returns {Promise<Object>} The imported module.
 * @throws {Error} If the component is not found.
 */
async function importModuleForAdminComponent(componentName) {
    switch (componentName) {
      case 'AboutUsSection':
        return import('./Admin/enci.portal.aboutus.js');
      case 'AdvisorySection':
        return import('./Admin/enci.portal.advisory.js');
      case 'AgreementsTrackerAdmin':
        return import('./Admin/enci.agreements.js');
      case 'AssignmentsAdmin':
      case 'AssignmentsTrackerAdmin':
        return import('./Admin/enci.assignments.js');
      case 'CalendarFullSection':
      case 'CalendarSplitSection':
        return import('./Admin/enci.portal.calendar.js');
      case 'CronjobResults':
        return import('./Admin/enci.cronjob.js');
      case 'DatabaseLogResults':
        return import('./Admin/enci.databaselogs.js');
      case 'EmailFormSection':
        return import('./Admin/enci.emailtemplates.js');
      case 'EmployeeSection':
        return import('./Admin/enci.employee.js');
      case 'FileCarouselSection':
        return import('./Admin/enci.portal.files.js');
      case 'GitlabDashboard':
        return import('./Admin/enci.gitlabdashboard.js');
      // case 'MainLayout':
      case 'HeaderSection':
        return import('./Admin/enci.portal.header.js');
      case 'HeroSection':
        return import('./Admin/enci.portal.heroes.js');
      case 'LotStatusTrackerAdmin':
        return import('./Admin/enci.lotstatustracker.js');
      case 'MetricSection':
        return import('./Admin/enci.portal.metrics.js');
      case 'MarketingStatusTracker':
        return import('./Admin/enci.marketingstatus.js');
      case 'NewsArticleSection':
        return import('./Admin/enci.portal.newsarticle.js');
      case 'NotableSaleSection':
        return import('./Admin/enci.portal.notablesales.js');
      case 'PageSection':
        return import('./Admin/enci.portal.pages.js');
      case 'ParticipationSection':
        return import('./Admin/enci.portal.participation.js');
      case 'PlatformSection':
        return import('./Admin/enci.portal.platforms.js');
      case 'Sage':
        return import('./Admin/enci.sage.js');
      case 'SellerLogoSection':
        return import('./Admin/enci.portal.sellerlogos.js');
      case 'StatsDashboard':
        return import('./Admin/enci.statsdashboard.js');
      case 'TeamSection':
      case 'TeamMasonrySection':
        return import('./Admin/enci.portal.contacts.js');
      case 'VdrEventsDashboard':
        return import('./Admin/enci.vdrevents.js');
      case 'Workflow':
        return import('./Admin/enci.workflow.js');
      default:
        throw new Error('Component not found');
    }
}

async function executeModuleLoad(module, methodName, name, isAdmin, cssSelector, config) {
    if (module && typeof module[methodName] === 'function') {
        await module[methodName](isAdmin, cssSelector, config);
        debugLog(`${methodName} >> Loaded successfully: ${name}`);
    } else {
        console.error(`${methodName} >> Module for ${name} does not have a ${methodName} method`);
    }
}

/**
 * Dynamically sets the website's theme based on the current URL route.
 * Applies a specific data-theme attribute to the body tag based on the route.
 * @returns {Promise<void>}
 */
export async function initializeThemeRoute() {
    debugLog('initializeThemeRoute');

    const currentRoute = window.location.pathname.toLowerCase();
    const themes = {
        efficientmarkets: 'efficientmarkets',
        vdr: 'efficientmarkets',
        energynet: 'energynet',
        indigoenergy: 'indigo',
        renovanexa: 'renova',
        verolands: 'vero',
        afe: 'afe',
        minerals: 'minerals',
        timber: 'timber',
        alaska: 'alaska',
        governmentresources: 'government',
        governmentlistings: 'government',
        calendar: 'government',
        admin: 'admin',
        maintenance: 'admin',
    };

    const theme = Object.keys(themes).find(key => currentRoute.includes(key)) || 'efficientmarkets';
    document.body.setAttribute('data-theme', themes[theme]);
}

export async function cleanupComponent(componentName) {
    debugLog(`cleanupComponent >> Cleaning up component: ${componentName}`);
    // Remove any event listeners or cleanup any resources
    // This is component-specific, so you might need to implement cleanup
    // functions for each component if necessary
}